import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ContentPagination } from "@/v2/services/types/serviceInterfaces";
import { Database } from "@/integrations/supabase/types";

type ReportDownload = Database["public"]["Tables"]["report_downloads"]["Row"];

interface UseReportDownloadsParams {
  page: number;
  pageSize: number;
  searchTerm?: string;
  marketingConsentFilter?: "all" | "yes" | "no";
}

interface ReportDownloadsStats {
  totalDownloads: number;
  marketingOptIns: number;
}

interface UseReportDownloadsResult {
  data: ReportDownload[] | undefined;
  pagination: ContentPagination | undefined;
  stats: ReportDownloadsStats | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useReportDownloads({
  page,
  pageSize,
  searchTerm = "",
  marketingConsentFilter = "all",
}: UseReportDownloadsParams): UseReportDownloadsResult {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "report-downloads",
      page,
      pageSize,
      searchTerm,
      marketingConsentFilter,
    ],
    queryFn: async () => {
      const offset = (page - 1) * pageSize;

      // Build the count query
      let countQuery = supabase
        .from("report_downloads")
        .select("*", { count: "exact", head: true });

      // Build the data query
      let dataQuery = supabase
        .from("report_downloads")
        .select("*")
        .order("created_at", { ascending: false })
        .range(offset, offset + pageSize - 1);

      // Apply marketing consent filter
      if (marketingConsentFilter === "yes") {
        countQuery = countQuery.eq("marketing_consent", true);
        dataQuery = dataQuery.eq("marketing_consent", true);
      } else if (marketingConsentFilter === "no") {
        countQuery = countQuery.eq("marketing_consent", false);
        dataQuery = dataQuery.eq("marketing_consent", false);
      }

      // Apply search filter
      if (searchTerm.trim()) {
        const searchPattern = searchTerm.trim();
        countQuery = countQuery.or(
          `name.ilike.%${searchPattern}%,email.ilike.%${searchPattern}%,linkedin_url.ilike.%${searchPattern}%`
        );
        dataQuery = dataQuery.or(
          `name.ilike.%${searchPattern}%,email.ilike.%${searchPattern}%,linkedin_url.ilike.%${searchPattern}%`
        );
      }

      // Fetch stats (total downloads and marketing opt-ins) - only if no filters applied
      let stats: ReportDownloadsStats | undefined;
      if (marketingConsentFilter === "all" && !searchTerm.trim()) {
        const [totalCount, marketingCount] = await Promise.all([
          supabase
            .from("report_downloads")
            .select("*", { count: "exact", head: true }),
          supabase
            .from("report_downloads")
            .select("*", { count: "exact", head: true })
            .eq("marketing_consent", true),
        ]);

        stats = {
          totalDownloads: totalCount.count || 0,
          marketingOptIns: marketingCount.count || 0,
        };
      }

      // Execute queries
      const [countResult, dataResult] = await Promise.all([
        countQuery,
        dataQuery,
      ]);

      if (countResult.error) throw new Error(countResult.error.message);
      if (dataResult.error) throw new Error(dataResult.error.message);

      const totalCount = countResult.count || 0;
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      const hasNextPage = page < totalPages;
      const hasPrevPage = page > 1;

      const pagination: ContentPagination = {
        currentPage: page,
        totalPages,
        hasNextPage,
        hasPrevPage,
        totalCount,
      };

      return {
        data: dataResult.data as ReportDownload[],
        pagination,
        stats,
      };
    },
  });

  return {
    data: data?.data,
    pagination: data?.pagination,
    stats: data?.stats,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Fetches all report downloads matching the provided filters (no pagination)
 * Used for CSV export
 */
export async function fetchAllReportDownloads({
  searchTerm = "",
  marketingConsentFilter = "all",
}: {
  searchTerm?: string;
  marketingConsentFilter?: "all" | "yes" | "no";
}): Promise<ReportDownload[]> {
  // Build the query
  let query = supabase
    .from("report_downloads")
    .select("*")
    .order("created_at", { ascending: false });

  // Apply marketing consent filter
  if (marketingConsentFilter === "yes") {
    query = query.eq("marketing_consent", true);
  } else if (marketingConsentFilter === "no") {
    query = query.eq("marketing_consent", false);
  }

  // Apply search filter
  if (searchTerm.trim()) {
    const searchPattern = searchTerm.trim();
    query = query.or(
      `name.ilike.%${searchPattern}%,email.ilike.%${searchPattern}%,linkedin_url.ilike.%${searchPattern}%`
    );
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message || "Failed to fetch report downloads");
  }

  return (data as ReportDownload[]) || [];
}
